import { Link } from 'react-router';
import { Brand } from '../components/Brand.js';

const SUPPORT_EMAIL = 'contact@ahmedkhelifi.com';
const UPDATED = '11 August 2026';

function OperatorAddress() {
  return (
    <address>
      Ahmed Khelifi
      <br />
      Leinestraße 20
      <br />
      12049 Berlin
      <br />
      Germany
      <br />
      Email: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
    </address>
  );
}

function PrivacyNotice() {
  return (
    <>
      <h2>1. Controller</h2>
      <p>The controller responsible for Bikepacking Live is:</p>
      <OperatorAddress />

      <h2>2. Data we process</h2>
      <p>Depending on how you use the service, we process:</p>
      <ul>
        <li>
          Account and authentication data, including your email address, one-time-code records,
          sessions, devices, app version, and operating-system version.
        </li>
        <li>
          Trip content, including journey and stage titles, descriptions, dates, notes, sharing
          settings, and statistics.
        </li>
        <li>
          Precise timestamped location, altitude, speed, heading, accuracy, and recorder state only
          while you explicitly record a stage. The native app may keep an encrypted or app-protected
          local queue until it can synchronize.
        </li>
        <li>
          Subscription and transaction status, product identifiers, renewal state, and provider
          customer identifiers. We do not receive complete card details.
        </li>
        <li>
          Security and service records, including pseudonymous IP-derived rate-limit values, request
          identifiers, webhook records, diagnostic events, and audience-capacity records.
        </li>
        <li>
          If you leave a viewer comment, the display name and plain-text message you choose,
          together with a pseudonymous viewer identifier used for rate limiting, self-removal, Terms
          acknowledgement, reports, device-level mutes, and cyclist blocks. Reports record a reason
          and moderation status, but not a raw cookie, access token, IP address, or private
          follower-link secret.
        </li>
      </ul>

      <h2>3. Why we process it</h2>
      <p>
        We process account, trip, location, sharing, and subscription data to provide the service
        and your membership under Article 6(1)(b) GDPR. We process limited security, abuse
        prevention, reliability, and diagnostic data under our legitimate interests in operating a
        safe and dependable service under Article 6(1)(f) GDPR. Where law requires records or a
        response to authorities, Article 6(1)(c) GDPR applies.
      </p>
      <p>
        There is no advertising, cross-company tracking, sale of personal data, or automated
        decision-making producing legal or similarly significant effects.
      </p>

      <h2>4. Live sharing</h2>
      <p>
        Followers do not need an account. When you activate a private trip link, anyone holding that
        link, and the passcode if you enabled one, can see the live route you chose to publish. A
        follower receives only the location data prepared for that shared view. On Unlimited
        journeys, followers can also post comments that are visible to anyone with the trip link.
        Treat the link as sensitive and rotate or stop it if it is shared too widely.
      </p>

      <h2>5. Service providers and transfers</h2>
      <ul>
        <li>Hetzner hosts the application and PostgreSQL database in Germany.</li>
        <li>AWS Simple Email Service delivers authentication and service email.</li>
        <li>
          RevenueCat, Apple, Google, and RevenueCat’s payment provider where web billing is used
          process subscriptions and purchase status.
        </li>
        <li>
          Map tiles, glyphs, and sprites are served by the operator-controlled map.ahmed.wtf
          service.
        </li>
      </ul>
      <p>
        Some providers may process data outside the EEA. Where required, transfers rely on an
        adequacy decision or contractual safeguards supplied by the provider. Their own privacy
        notices also apply to the accounts and payment relationships they control.
      </p>

      <h2>6. Retention and deletion</h2>
      <ul>
        <li>Authentication codes and short-lived rate-limit records are removed after two days.</li>
        <li>
          Expired or revoked account sessions are removed after their operational retention period;
          follower access sessions are removed sooner.
        </li>
        <li>
          Trips and precise routes remain while your account is active so you can view and export
          them. You can delete individual trips at any time.
        </li>
        <li>
          Viewer comments remain with the shared trip until removed by their author or the cyclist,
          or until the associated trip, share link, or cyclist account is deleted.
        </li>
        <li>
          Comment Terms acknowledgements, mutes, and blocks remain with that follower link. Resolved
          abuse reports and their audit status are retained for up to 24 months. Open reports remain
          until reviewed or the associated trip, link, or account is deleted.
        </li>
        <li>
          Audience audit records are retained for up to 13 months, and billing/security audit
          records for up to 24 months.
        </li>
        <li>
          Account deletion immediately stops sharing and signs devices out. Primary account, trip,
          and location data is deleted within 30 days. Encrypted backup copies expire within 90 days
          and are not restored for ordinary product use.
        </li>
      </ul>
      <p>
        Apple, Google, RevenueCat, payment providers, and legally required financial records may
        have separate retention duties. Cancel an active membership before deleting the account.
      </p>

      <h2>7. Your choices and rights</h2>
      <p>
        You can pause or finish recording, stop or rotate sharing, report a viewer comment, mute its
        author in your browser, remove your own comment, export GPX/account data, delete trips, and
        request account deletion in the app. A cyclist can hide a comment, block that viewer
        identity from comments, or turn comments off. The same account-deletion process is available
        through the <Link to="/delete-account">public web page</Link>.
      </p>
      <p>
        Subject to applicable law, you may request access, correction, deletion, restriction,
        portability, or object to processing based on legitimate interests. You may also complain to
        a competent data-protection authority, including the Berlin Commissioner for Data Protection
        and Freedom of Information. Contact us at{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>

      <h2>8. Security, children, and changes</h2>
      <p>
        Transport is encrypted with HTTPS, sensitive identifiers are hashed, account email is
        encrypted in the database, and application logs are designed to redact precise coordinates
        and credentials. No internet service can guarantee absolute security.
      </p>
      <p>
        Bikepacking Live is not directed to children. Do not use it to publish another person’s
        precise location without their informed permission. Material changes to this notice will be
        posted here with a new date.
      </p>
    </>
  );
}

function TermsOfService() {
  return (
    <>
      <h2>1. Scope and account</h2>
      <p>
        These terms apply to Bikepacking Live, operated by Ahmed Khelifi. You must be at least 18
        and able to enter a contract to buy a membership. You are responsible for access to your
        email account, devices, private trip links, and passcodes.
      </p>

      <h2>2. The service</h2>
      <p>
        Bikepacking Live lets a cyclist organize a multi-stage journey, record stages, calculate
        statistics, and share a private live or completed route with invited followers. Followers do
        not pay and do not need an account. Unlimited journeys can accept plain-text viewer comments
        that everyone holding the private trip link can read.
      </p>

      <h2>3. Not an emergency or navigation service</h2>
      <p>
        Do not rely on Bikepacking Live to summon help, determine a cyclist’s current condition,
        provide navigation, or serve as the only safety plan. GPS accuracy, phone settings, battery,
        mobile networks, server availability, and browsers can interrupt or delay updates. Always
        use appropriate safety equipment, route planning, emergency contacts, and judgment.
      </p>

      <h2>4. Membership and payment</h2>
      <p>
        The Free service supports private trip planning, stage organization, ride recording, maps,
        and statistics. Publishing a live follower link requires a membership. Bikepacker supports
        up to 100 distinct live followers. Bikepacker Unlimited has no fixed live-audience cap and
        enables viewer comments, but fair-use, security, and infrastructure protections still apply.
        The localized price, taxes, billing period, trial if any, and provider terms shown before
        purchase are authoritative.
      </p>
      <p>
        Membership renews monthly until cancelled. Manage or cancel it through the Apple App Store,
        Google Play, or the web billing portal used for the purchase. Cancellation stops future
        renewal and normally leaves access active until the paid period ends. Refunds and statutory
        withdrawal rights follow applicable law and the payment provider’s process. Deleting a
        Bikepacking Live account does not reliably cancel every provider subscription, so cancel
        first.
      </p>

      <h2>5. Acceptable use</h2>
      <p>You may not use the service to:</p>
      <ul>
        <li>track or publish another person’s location without informed permission;</li>
        <li>harass, threaten, deceive, stalk, or facilitate unlawful activity;</li>
        <li>upload unlawful content or content you do not have the right to use;</li>
        <li>bypass viewer limits, access controls, rate limits, or security measures; or</li>
        <li>interfere with the service or other users’ access.</li>
      </ul>
      <p>
        We may restrict abusive access or suspend an account when reasonably necessary to protect
        users, the service, or comply with law. We will provide notice where practical and legally
        permitted.
      </p>

      <h2>6. Viewer comments and moderation</h2>
      <p>
        Before posting an anonymous viewer comment, you must accept the current comment Terms and
        keep the message respectful and safe. Do not post threats, hate, sexual abuse, harassment,
        spam, unlawful material, private information, or content that puts anyone at risk. A
        conservative automated filter can reject high-confidence abuse before publication.
      </p>
      <p>
        Anyone holding the private trip link can read a published comment unless they mute its
        author. Viewers can report a comment and remove their own comment. The cyclist can hide a
        comment, block that pseudonymous viewer identity from reading or posting comments on the
        follower link, or turn comments off. Bikepacking Live receives a limited moderation alert
        for reports and may review, remove, restrict, preserve, or disclose content when reasonably
        necessary for safety or law. Contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>{' '}
        about urgent abuse.
      </p>

      <h2>7. Availability and changes</h2>
      <p>
        We work to provide a reliable service, but uninterrupted or error-free operation is not
        guaranteed. We may maintain, secure, or improve the service. Material changes affecting a
        paid membership will be communicated as required by law. You retain access to export and
        deletion controls even when you do not have an active membership.
      </p>

      <h2>8. Responsibility</h2>
      <p>
        Statutory liability applies, including liability that cannot legally be limited. Nothing in
        these terms excludes liability for intent, gross negligence, injury to life, body or health,
        fraudulent concealment, or mandatory product-liability and consumer rights. For ordinary
        negligence, liability is limited as permitted by law to foreseeable damage from breach of an
        essential contractual duty.
      </p>

      <h2>9. Ending use and governing law</h2>
      <p>
        You may stop using the service, cancel membership, export data, stop sharing, and delete
        your account at any time. German law applies, without removing mandatory consumer
        protections or jurisdiction rights in your country of residence.
      </p>

      <h2>10. Contact and dispute resolution</h2>
      <p>
        Contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> so we can try to resolve a
        concern directly. The operator is neither obliged nor willing to participate in dispute
        resolution proceedings before a consumer arbitration board.
      </p>
    </>
  );
}

function Imprint() {
  return (
    <>
      <h2>Information under § 5 DDG</h2>
      <OperatorAddress />

      <h2>Responsible for editorial content under § 18(2) MStV</h2>
      <OperatorAddress />

      <h2>Consumer dispute resolution</h2>
      <p>
        The operator is neither obliged nor willing to participate in dispute resolution proceedings
        before a consumer arbitration board.
      </p>
    </>
  );
}

export function LegalPage({ kind }: { kind: 'privacy' | 'terms' | 'imprint' }) {
  const title =
    kind === 'privacy' ? 'Privacy notice' : kind === 'terms' ? 'Terms of service' : 'Imprint';
  return (
    <div className="legal-page">
      <header>
        <Brand />
        <Link to="/">Back</Link>
      </header>
      <main>
        <p className="eyebrow">Legal</p>
        <h1>{title}</h1>
        <p>Last updated {UPDATED}</p>
        {kind === 'privacy' ? (
          <PrivacyNotice />
        ) : kind === 'terms' ? (
          <TermsOfService />
        ) : (
          <Imprint />
        )}
      </main>
    </div>
  );
}
